import React from 'react';
import _ from 'underscore';

import {BaseMigrationObjectPlan} from 'config-migration/types/MigrationObjects';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

import {ObjectAction, ObjectActionType} from '../types/objectActions';
import {isInaccessibleUpdateAction} from './planning-logic/convertImportIdentifiersToPlans';

interface ActionDropdownProps {
  object: BaseMigrationObjectPlan;
  onUpdateObject: (object: BaseMigrationObjectPlan) => void;
  excludeIgnore?: boolean;
  showActionInline?: boolean;
}

export function getActionDescription(action: ObjectAction): JSX.Element | string {
  if (action.type === ObjectActionType.CREATE) {
    return 'Create';
  }
  if (action.type === ObjectActionType.IGNORE) {
    // Append reason if present
    if (typeof action.reason === 'string' && action.reason != null) {
      return `Ignore (${action.reason})`;
    }
    return 'Ignore';
  } else {
    return (
      <span>
        Update <strong>{action.target.displayName}</strong>
        {action.target.archived && ` (Archived)`}
      </span>
    );
  }
}

export function getOptionValue(action: ObjectAction): string {
  return action.type === ObjectActionType.UPDATE ? action.target.apiIdentifier : action.type;
}

export function getActionOption(action: ObjectAction, onSelect: (action: ObjectAction) => void): JSX.Element {
  return (
    <Option
      value={getOptionValue(action)}
      onSelect={() => {
        onSelect(action);
      }}
      key={_.uniqueId('action-')}
    >
      {getActionDescription(action)}
    </Option>
  );
}

export default function ObjectActionDropdown(props: ActionDropdownProps): JSX.Element {
  const updateObject = (selectedAction: ObjectAction): void => {
    props.onUpdateObject({...props.object, selectedAction});
  };

  // Compute filtered actions for dropdown
  const accessibleActions = props.object.additionalActions.filter((a) => !isInaccessibleUpdateAction(a));
  const inaccessibleIgnores = props.object.additionalActions.filter((action) =>
    isInaccessibleUpdateAction(action)
  );
  const allAreInaccessible = props.object.additionalActions.length > 0 && accessibleActions.length === 0;
  const filteredAdditionalActions = allAreInaccessible ? inaccessibleIgnores : props.object.additionalActions;
  const showIgnore = !props.excludeIgnore && (!allAreInaccessible || filteredAdditionalActions.length === 0);

  // Count actual available actions
  const availableActions = [
    props.object.hasProvenanceUriMatch ? props.object.initialAction : {type: ObjectActionType.CREATE},
    ...(showIgnore ? [{type: ObjectActionType.IGNORE}] : []),
    ...(!props.object.hasProvenanceUriMatch ? filteredAdditionalActions : []),
  ];

  if (props.showActionInline) {
    return (
      <div className="u-mini-spacing-right u-flex-grow">
        {getActionDescription(props.object.selectedAction)}
      </div>
    );
  }
  return (
    <SelectDropdown
      className="u-mini-spacing-right u-flex-grow"
      disabled={availableActions.length <= 1}
      menu={
        <Menu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          value={getOptionValue(props.object.selectedAction)}
          scrollable={true}
        >
          <Section>
            {getActionOption(
              props.object.hasProvenanceUriMatch
                ? props.object.initialAction
                : {type: ObjectActionType.CREATE},
              updateObject
            )}
            {showIgnore && getActionOption({type: ObjectActionType.IGNORE}, updateObject)}
          </Section>
          <Section>
            {!props.object.hasProvenanceUriMatch &&
              filteredAdditionalActions.map((action) => getActionOption(action, updateObject))}
          </Section>
        </Menu>
      }
      displayName={getActionDescription(props.object.selectedAction)}
    />
  );
}
